import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";
import { apiFetch } from "@/lib/api";
import { useInitialData } from "@/lib/initial-data-context";
import type { BlogPostSummary } from "@/lib/initial-data";
import { Compass, Sun, CloudRain, PawPrint, ArrowRight, Phone, BookOpen } from "lucide-react";

const BOOK_A_CALL = "https://calendly.com/carla-tallylassie";

const resources = [
  {
    eyebrow: "Interactive tool",
    title: "Care Plan Finder",
    description:
      "Answer five quick questions and get a personalized service recommendation with a price range — matched to your pet and your schedule.",
    href: "/care-plan-finder",
    icon: Compass,
    featured: true,
    cta: "Find my plan",
  },
  {
    eyebrow: "Free guide",
    title: "Tallahassee Summer Dog Safety",
    description:
      "Beating our brutal heat and humidity — hot-pavement checks, hydration, peak-hour timing, and the warning signs of heatstroke.",
    href: "/resources/summer-safety",
    icon: Sun,
    cta: "Read the guide",
  },
  {
    eyebrow: "Free guide",
    title: "Hurricane & Storm Prep for Pets",
    description:
      "A Leon County pet-parent checklist: go-bags, microchips and records, evacuation planning, and what to do when you can't be home.",
    href: "/resources/hurricane-prep",
    icon: CloudRain,
    cta: "Read the guide",
  },
  {
    eyebrow: "Free guide",
    title: "New Puppy & Rescue Welcome Kit",
    description:
      "Your first two weeks with a new dog in Tallahassee — supply checklist, the 3-3-3 decompression rule, house-training rhythm, and local resources.",
    href: "/resources/new-pet-welcome-kit",
    icon: PawPrint,
    cta: "Read the guide",
  },
];

export default function Resources() {
  const { blogPosts: initialPosts } = useInitialData();
  const [latestPosts, setLatestPosts] = useState<BlogPostSummary[]>(
    () => initialPosts?.slice(0, 3) ?? [],
  );
  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

  useEffect(() => {
    if (initialPosts) return;

    apiFetch("/blog/posts")
      .then((posts: BlogPostSummary[]) => setLatestPosts(posts.slice(0, 3)))
      .catch(() => {});
  }, [initialPosts]);

  function getImageUrl(imagePath: string) {
    return imagePath.startsWith("/objects")
      ? `${BASE}/api/storage${imagePath}`
      : imagePath;
  }

  return (
    <div className="flex flex-col w-full">
      <SEO
        title="Pet Parent Resources | Guides, Tools & Articles"
        description="Free pet care resources for Tallahassee pet parents: practical guides, a personalized Care Plan Finder, and the latest articles from the Tally Lassie Times."
        path="/resources"
      />

      <section className="relative bg-primary text-primary-foreground py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/b885628e-de6b-49d7-bf64-f592f9783add/DogWalker.jpg')] bg-cover bg-center opacity-[0.12] mix-blend-overlay"></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 text-center max-w-3xl">
          <Reveal>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Pet Parent Resources
            </h1>
          </Reveal>
          <Reveal delay={150}>
            <p className="text-xl text-primary-foreground/85 leading-relaxed">
              Free, genuinely useful tools and local guides from your Tallahassee neighbors — made to help you care for your pets, whether or not you ever book with us.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-6">
            {resources.map((r, i) => {
              const Icon = r.icon;
              return (
                <Reveal key={i} delay={i * 60}>
                  <div
                    className={`group h-full flex flex-col p-8 border transition-all card-lift ${
                      r.featured
                        ? "bg-primary/5 border-primary/20 md:col-span-2"
                        : "bg-card border-border/60 hover:border-primary/30"
                    }`}
                    style={{ borderRadius: "24px 0 24px 0" }}
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <span
                        className={`w-12 h-12 shrink-0 flex items-center justify-center shadow-sm ${
                          r.featured ? "bg-primary text-white" : "bg-primary/10 text-primary"
                        }`}
                        style={{ borderRadius: "12px 0 12px 0" }}
                      >
                        <Icon size={24} />
                      </span>
                      <span className="uppercase tracking-[0.18em] text-xs font-semibold text-primary/70">
                        {r.eyebrow}
                      </span>
                    </div>
                    <h2 className="font-serif text-2xl font-bold text-foreground mb-3">{r.title}</h2>
                    <p className="text-foreground/75 leading-relaxed mb-6 flex-1">{r.description}</p>
                    <Button
                      asChild
                      className={
                        r.featured
                          ? ""
                          : "bg-transparent text-primary border border-primary hover:bg-primary hover:text-white"
                      }
                    >
                      <Link href={r.href}>
                        {r.cta}{" "}
                        <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {latestPosts.length > 0 && (
            <section className="mt-16 border-t border-border/70 pt-14" aria-labelledby="latest-articles-heading">
              <Reveal>
                <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <div className="mb-3 flex items-center gap-2 text-primary">
                      <BookOpen size={20} />
                      <span className="text-xs font-semibold uppercase tracking-[0.18em]">
                        Fresh pet care insights
                      </span>
                    </div>
                    <h2
                      id="latest-articles-heading"
                      className="font-serif text-3xl font-bold text-foreground"
                    >
                      Latest from Tally Lassie Times
                    </h2>
                  </div>
                  <Link href="/blog" className="inline-flex items-center font-semibold text-primary hover:text-primary/80">
                    View all articles <ArrowRight size={16} className="ml-2" />
                  </Link>
                </div>
              </Reveal>

              <div className="grid gap-6 md:grid-cols-3">
                {latestPosts.map((post, index) => {
                  const createdAt = new Date(post.createdAt);
                  const updatedAt = post.updatedAt ? new Date(post.updatedAt) : createdAt;
                  const wasUpdated = updatedAt.getTime() > createdAt.getTime() + 1000;
                  const displayDate = wasUpdated ? updatedAt : createdAt;

                  return (
                    <Reveal key={post.id} delay={index * 60}>
                      <Link href={`/blog/${post.slug}`}>
                        <article
                          className="group h-full overflow-hidden border border-border/60 bg-card transition-all hover:border-primary/30 hover:shadow-md"
                          style={{ borderRadius: "20px 0 20px 0" }}
                        >
                          {post.coverImage && (
                            <img
                              src={getImageUrl(post.coverImage)}
                              alt=""
                              className="aspect-[16/9] w-full object-cover"
                            />
                          )}
                          {!post.coverImage && (
                            <div className="flex aspect-[16/9] items-center justify-center bg-primary/5 text-primary/45">
                              <BookOpen size={40} />
                            </div>
                          )}
                          <div className="p-6">
                            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              {wasUpdated ? "Updated" : "Published"}{" "}
                              {displayDate.toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </p>
                            <h3 className="mb-3 font-serif text-xl font-bold leading-snug text-foreground transition-colors group-hover:text-primary">
                              {post.title}
                            </h3>
                            {post.excerpt && (
                              <p className="line-clamp-4 text-sm leading-relaxed text-foreground/70">
                                {post.excerpt}
                              </p>
                            )}
                          </div>
                        </article>
                      </Link>
                    </Reveal>
                  );
                })}
              </div>
            </section>
          )}

          <Reveal>
            <div
              className="mt-16 bg-primary text-white p-10 md:p-12 text-center relative overflow-hidden"
              style={{ borderRadius: "32px 0 32px 0" }}
            >
              <h2 className="font-serif text-2xl md:text-3xl font-bold mb-4">
                Prefer to just talk it through?
              </h2>
              <p className="text-lg text-white/90 max-w-xl mx-auto mb-8">
                Book a free, no-pressure call with Carla and we'll help you figure out the right care for your pet.
              </p>
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 h-14 px-8 text-lg">
                <a href={BOOK_A_CALL} target="_blank" rel="noopener noreferrer">
                  <Phone size={18} className="mr-2" /> Book a Free Call
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
